import { cn, statusStyles, linkTypeStyles, getStatusStyle, getLinkTypeStyle } from '../lib/utils';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  AlertCircle,
  ArrowRight,
  Clock,
  ServerCrash,
  Timer,
  Loader2,
} from 'lucide-react';

/**
 * Status icon mapping
 */
const statusIcons = {
  removed: XCircle,
  '404': AlertTriangle,
  '5xx': ServerCrash,
  nofollow_changed: AlertCircle,
  redirected: ArrowRight,
  timeout: Timer,
  pending: Clock,
  checking: Loader2,
  live: CheckCircle,
};

/**
 * StatusBadge - Display backlink status with semantic colors
 *
 * @param {string} status - Backlink status
 * @param {boolean} showIcon - Show status icon
 * @param {boolean} showLabel - Show status label
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {string} variant - 'default' | 'outline' | 'solid'
 */
export function StatusBadge({
  status,
  showIcon = true,
  showLabel = true,
  size = 'md',
  variant = 'default',
  className,
}) {
  const style = getStatusStyle(status);
  const Icon = statusIcons[status] || Clock;

  // Size variants
  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-xs gap-1.5',
    lg: 'px-3 py-1.5 text-sm gap-2',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  // Variant styles
  const variantStyles = {
    default: cn(
      style.bgLight || style.bg,
      style.textLight || style.text,
      style.border ? `border ${style.border}` : ''
    ),
    outline: cn(
      'bg-transparent',
      style.textLight || style.text,
      `border ${style.border || 'border-current'}`
    ),
    solid: cn(
      style.bg,
      style.text
    ),
  };

  // Critical statuses get solid styling by default
  const isCritical = ['removed', '404', '5xx'].includes(status);
  const effectiveVariant = isCritical && variant === 'default' ? 'solid' : variant;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md whitespace-nowrap font-medium',
        sizeStyles[size],
        variantStyles[effectiveVariant],
        style.animate,
        className
      )}
    >
      {showIcon && Icon && (
        <Icon
          className={cn(
            iconSizes[size],
            'flex-shrink-0',
            status === 'checking' && 'animate-spin'
          )}
        />
      )}
      {showLabel && <span>{style.label}</span>}
    </span>
  );
}

/**
 * LinkTypeBadge - Display link type (dofollow/nofollow/etc.)
 *
 * @param {string} type - Link type
 */
export function LinkTypeBadge({ type, className }) {
  const style = getLinkTypeStyle(type);

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
        style.bg,
        style.text,
        className
      )}
    >
      {style.label}
    </span>
  );
}

/**
 * QualityBadge - Display quality score with color coding
 *
 * @param {number} score - Quality score (0-1)
 */
export function QualityBadge({ score, className }) {
  const numScore = parseFloat(score) || 0;

  let config;
  if (numScore >= 0.6) {
    config = { bg: 'bg-success/20', text: 'text-success', label: 'High' };
  } else if (numScore >= 0.35) {
    config = { bg: 'bg-warning/20', text: 'text-warning', label: 'Medium' };
  } else {
    config = { bg: 'bg-destructive/20', text: 'text-destructive', label: 'Low' };
  }

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
        config.bg,
        config.text,
        className
      )}
    >
      {config.label} ({Math.round(numScore * 100)}%)
    </span>
  );
}

/**
 * SpamBadge - Display spam score with inverted color coding
 *
 * @param {number} score - Spam score
 * @param {number} maxScale - Max scale (17 for Moz, 100 for DFS)
 */
export function SpamBadge({ score, maxScale = 17, className }) {
  if (score == null) {
    return (
      <span className={cn('text-xs text-muted-foreground', className)}>-</span>
    );
  }

  const normalized = maxScale === 100 ? score : (score / maxScale) * 100;

  let config;
  if (normalized >= 60) {
    config = { bg: 'bg-destructive/20', text: 'text-destructive', label: 'High' };
  } else if (normalized >= 30) {
    config = { bg: 'bg-warning/20', text: 'text-warning', label: 'Medium' };
  } else {
    config = { bg: 'bg-success/20', text: 'text-success', label: 'Low' };
  }

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
        config.bg,
        config.text,
        className
      )}
    >
      {Math.round(score)}{maxScale === 100 ? '' : `/${maxScale}`}
    </span>
  );
}

/**
 * Status priority for sorting
 */
export function getStatusPriority(status) {
  return statusStyles[status]?.priority || 10;
}

/**
 * Get all statuses grouped by priority
 */
export function getStatusGroups() {
  return {
    critical: ['removed', '404', '5xx'],
    warning: ['nofollow_changed', 'redirected', 'timeout'],
    info: ['pending', 'checking'],
    success: ['live'],
  };
}

// Default export for backwards compatibility
export default StatusBadge;
